import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Map, { Layer, Source, type MapRef } from 'react-map-gl';
import type { FillLayer, LineLayer } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { area, polygon } from '@turf/turf';
import { ExternalLink, Layers, Satellite } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ensureClosedRing, footprintPolygon, ringLonLatFromGeoSource } from '../utilities/normalizeGeoShape';
import { isOnPlot } from './parcelPlot';
import { ParcelHoverSource } from './parcelHover';
import ParcelTooltip, { useParcelHover } from './ParcelTooltip';
import { MAPBOX_TOKEN } from '../utilities/mapbox';

/**
 * The picked property at a glance: its footprint drawn in red on a small map
 * (switchable to satellite imagery, same as the building's location tab), next
 * to the core figures the Building carries.
 */


const FALLBACK_VIEW = { longitude: 8.669347, latitude: 50.106277, zoom: 15 };

const numberFormat0 = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 });
const numberFormat1 = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 });

interface Properties {
    building: any;
    /** Address as it was picked — shown while the building carries none itself. */
    fallbackLabel?: string;
    /**
     * The parcels the site estimator assigned to the plot, drawn under the footprint. The same rows
     * the cadastral drill-down lists, so the map and the table cannot tell different stories.
     */
    parcels?: Array<{ parcelId: string; role: any; geometry: any } & ParcelHoverSource>;
}

interface KeyFact {
    label: string;
    value: string;
    unit?: string;
}

function viewStateFor(building: any) {
    if (!building) {
        return FALLBACK_VIEW;
    }

    const hasBounds =
        Number.isFinite(building.minLon) &&
        Number.isFinite(building.maxLon) &&
        Number.isFinite(building.minLat) &&
        Number.isFinite(building.maxLat);

    if (hasBounds) {
        return {
            longitude: 0.5 * (building.minLon + building.maxLon),
            latitude: 0.5 * (building.minLat + building.maxLat),
            zoom: 17,
        };
    }

    const ring = ringLonLatFromGeoSource(building);
    if (ring && ring.length) {
        const longitudes = ring.map((coordinate) => coordinate[0]);
        const latitudes = ring.map((coordinate) => coordinate[1]);
        return {
            longitude: (Math.min(...longitudes) + Math.max(...longitudes)) / 2,
            latitude: (Math.min(...latitudes) + Math.max(...latitudes)) / 2,
            zoom: 17,
        };
    }

    return FALLBACK_VIEW;
}

/** Bounding box of the footprint (or the building's own bounds) for framing the map. */
function boundsFor(building: any): [[number, number], [number, number]] | null {
    const ring = ringLonLatFromGeoSource(building);

    if (ring && ring.length >= 3) {
        const longitudes = ring.map((coordinate) => coordinate[0]);
        const latitudes = ring.map((coordinate) => coordinate[1]);
        return [
            [Math.min(...longitudes), Math.min(...latitudes)],
            [Math.max(...longitudes), Math.max(...latitudes)],
        ];
    }

    const hasBounds =
        Number.isFinite(building?.minLon) &&
        Number.isFinite(building?.maxLon) &&
        Number.isFinite(building?.minLat) &&
        Number.isFinite(building?.maxLat);

    return hasBounds
        ? [
              [building.minLon, building.minLat],
              [building.maxLon, building.maxLat],
          ]
        : null;
}

function footprintArea(building: any): number | undefined {
    const ring = ringLonLatFromGeoSource(building);
    if (!ring || ring.length < 3) {
        return undefined;
    }
    return area(polygon([ensureClosedRing(ring)]));
}

function footprintFeatureCollection(building: any) {
    const geometry = footprintPolygon(building);
    if (!geometry) {
        return { type: 'FeatureCollection', features: [] };
    }
    return {
        type: 'FeatureCollection',
        features: [{ type: 'Feature', properties: {}, geometry }],
    };
}

/**
 * The parcels as two collections: what is certain and what is still open.
 *
 * Drawn apart because they mean different things — a solid gold outline for the ground the building
 * stands on and the parcels assigned to it, a dashed one for what the estimate has not settled. A
 * single colour would present a guess as a boundary.
 */
function parcelFeatures(parcels: Properties['parcels'], settled: boolean) {
    // The same rule the drill-down draws and clicks by. It used to be a list of role names written
    // out here, which quietly dropped the parcels somebody had assigned by hand the day `manual`
    // was added: not settled, not open, drawn nowhere. A list has to be found and corrected; a
    // shared rule cannot fall behind in the first place.
    const wanted = (role: any) => (settled ? isOnPlot(role) : role === 'open');

    return {
        type: 'FeatureCollection',
        features: (parcels ?? [])
            .filter((parcel) => wanted(parcel.role) && parcel.geometry)
            .map((parcel) => ({
                type: 'Feature',
                properties: { parcelId: parcel.parcelId },
                geometry: parcel.geometry,
            })),
    };
}

export default function ObjectOverview({ building, fallbackLabel, parcels }: Properties) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [satellite, setSatellite] = useState(false);
    // One map instance for the whole page life. Remounting it per object (a key on
    // the building id) races the style load: a GeoJSON source added before the new
    // instance's style is up is silently dropped and the footprint never appears.
    // Instead the camera is moved and the source's data swapped in place.
    const [mapLoaded, setMapLoaded] = useState(false);
    const mapRef = useRef<MapRef | null>(null);
    const { hovered, onMouseMove, onMouseLeave } = useParcelHover();

    const view = useMemo(() => viewStateFor(building), [building]);
    const bounds = useMemo(() => boundsFor(building), [building]);

    // Frame the footprint rather than trusting a fixed zoom — the map is small,
    // and a large building would otherwise run out of the frame.
    useEffect(() => {
        const map = mapRef.current;
        if (!map) {
            return;
        }
        if (bounds) {
            map.fitBounds(bounds, { padding: 22, duration: 0, maxZoom: 18 });
        } else {
            map.jumpTo({ center: [view.longitude, view.latitude], zoom: view.zoom });
        }
    }, [bounds, view, mapLoaded]);
    const footprint = useMemo(() => footprintArea(building), [building]);
    const shape = useMemo(() => footprintFeatureCollection(building), [building]);
    const settledParcels = useMemo(() => parcelFeatures(parcels, true), [parcels]);
    const hoveredParcel = hovered
        ? (parcels ?? []).find((parcel) => parcel.parcelId === hovered.parcelId) ?? null
        : null;
    const openParcels = useMemo(() => parcelFeatures(parcels, false), [parcels]);

    const missing = '–';
    const number = (value: unknown, format: Intl.NumberFormat) =>
        Number.isFinite(Number(value)) && Number(value) !== 0 ? format.format(Number(value)) : missing;

    const facts: KeyFact[] = [
        {
            label: t('portfolioDashboard.keyFacts.footprintArea'),
            value: footprint === undefined ? missing : numberFormat0.format(footprint),
            unit: '㎡',
        },
        {
            // A roof seen from above covers the footprint — that is what is derived
            // here. The Building carries no surveyed roof area of its own.
            label: t('portfolioDashboard.keyFacts.roofArea'),
            value: footprint === undefined ? missing : numberFormat0.format(footprint),
            unit: '㎡',
        },
        {
            label: t('portfolioDashboard.keyFacts.height'),
            value: number(building?.height, numberFormat1),
            unit: 'm',
        },
        { label: t('portfolioDashboard.keyFacts.levels'), value: number(building?.levels, numberFormat0) },
        {
            label: t('portfolioDashboard.keyFacts.grossFloorArea'),
            value: number(building?.area, numberFormat0),
            unit: '㎡',
        },
        {
            // A year is not a quantity — no thousands separator.
            label: t('portfolioDashboard.keyFacts.yearBuilt'),
            value: Number.isFinite(Number(building?.yearBuilt)) && Number(building?.yearBuilt) !== 0
                ? String(Math.trunc(Number(building.yearBuilt)))
                : missing,
        },
        { label: t('portfolioDashboard.keyFacts.units'), value: number(building?.units, numberFormat0) },
        { label: t('portfolioDashboard.keyFacts.buildingType'), value: building?.buildingType || missing },
    ];

    const heading =
        [building?.street, building?.streetNumber].filter(Boolean).join(' ') ||
        building?.address?.street ||
        fallbackLabel ||
        building?.name ||
        '';

    return (
        <div className="grid items-stretch gap-3.5 md:grid-cols-[260px_minmax(0,1fr)]">
            <div className="relative overflow-hidden rounded-lg border">
                <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="absolute right-2 top-2 z-[2] gap-1.5 rounded-full shadow-sm"
                    onClick={() => setSatellite(!satellite)}
                    title={t(satellite ? 'portfolioDashboard.map.showMap' : 'portfolioDashboard.map.showSatellite')}
                >
                    {satellite ? <Layers size={14} /> : <Satellite size={14} />}
                    {t(satellite ? 'portfolioDashboard.map.showMap' : 'portfolioDashboard.map.showSatellite')}
                </Button>
                <Map
                    ref={mapRef}
                    mapboxAccessToken={MAPBOX_TOKEN}
                    initialViewState={view}
                    style={{ width: '100%', height: 170 }}
                    mapStyle={
                        satellite
                            ? 'mapbox://styles/mapbox/satellite-streets-v9'
                            : 'mapbox://styles/mapbox/streets-v9'
                    }
                    // The parcels answer the pointer here too: this map is read far more often
                    // than the drill-down is opened, and an outline nobody can name is a shape.
                    interactiveLayerIds={['portfolioParcelsFill', 'portfolioParcelsOpenFill']}
                    onMouseMove={onMouseMove}
                    onMouseLeave={onMouseLeave}
                    onLoad={() => setMapLoaded(true)}
                >
                    <ParcelTooltip hovered={hovered} parcel={hoveredParcel} />

                    {/* Parcels first: the footprint is the subject and stays on top of them. */}
                    {mapLoaded ? (
                        <Source id="portfolioParcels" type="geojson" data={settledParcels as any}>
                            <Layer
                                {...({
                                    id: 'portfolioParcelsFill',
                                    type: 'fill',
                                    paint: { 'fill-color': 'rgba(200, 162, 74, 0.18)' },
                                } as FillLayer)}
                            />
                            <Layer
                                {...({
                                    id: 'portfolioParcelsLine',
                                    type: 'line',
                                    paint: { 'line-color': '#7A5C1E', 'line-width': 1.2 },
                                } as LineLayer)}
                            />
                        </Source>
                    ) : null}
                    {mapLoaded ? (
                        <Source id="portfolioParcelsOpen" type="geojson" data={openParcels as any}>
                            {/* A dashed outline has nothing to hit-test against, so an unsettled
                                parcel would be the one the pointer could not name — and it is the
                                one a reader most wants named. Barely there, purely to answer. */}
                            <Layer
                                {...({
                                    id: 'portfolioParcelsOpenFill',
                                    type: 'fill',
                                    paint: { 'fill-color': '#C8A24A', 'fill-opacity': 0.06 },
                                } as FillLayer)}
                            />
                            <Layer
                                {...({
                                    id: 'portfolioParcelsOpenLine',
                                    type: 'line',
                                    paint: {
                                        'line-color': '#7A5C1E',
                                        'line-width': 1.2,
                                        // Dashed: not settled, and the map should say so.
                                        'line-dasharray': [2, 2],
                                    },
                                } as LineLayer)}
                            />
                        </Source>
                    ) : null}
                    {mapLoaded ? (
                        <Source id="portfolioFootprint" type="geojson" data={shape as any}>
                            <Layer
                                {...({
                                    id: 'portfolioFootprintFill',
                                    type: 'fill',
                                    paint: { 'fill-color': 'rgba(200, 30, 30, 0.45)' },
                                } as FillLayer)}
                            />
                            <Layer
                                {...({
                                    id: 'portfolioFootprintLine',
                                    type: 'line',
                                    paint: { 'line-color': '#b00000', 'line-width': 2 },
                                } as LineLayer)}
                            />
                        </Source>
                    ) : null}
                </Map>
            </div>
            <div className="min-w-0">
                <div className="mb-1 flex min-h-[26px] flex-wrap items-center gap-1 text-sm font-bold">
                    <span>{heading}</span>
                    {building?._id ? (
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground"
                            title={t('portfolioDashboard.openBuilding')}
                            aria-label={t('portfolioDashboard.openBuilding')}
                            onClick={() =>
                                navigate(
                                    `/entity?selectedEntityType=Building&selectedEntity=${encodeURIComponent(String(building._id))}`,
                                )
                            }
                        >
                            <ExternalLink className="h-4 w-4" />
                        </Button>
                    ) : (
                        <></>
                    )}
                </div>
                <div className="grid content-start gap-x-6 sm:grid-cols-2">
                    {facts.map((fact) => (
                        <div
                            key={fact.label}
                            className="flex items-baseline justify-between gap-2.5 border-b border-dashed py-1"
                        >
                            <span className="whitespace-nowrap text-xs uppercase tracking-wide text-muted-foreground">
                                {fact.label}
                            </span>
                            <span className="whitespace-nowrap text-sm font-bold">
                                {fact.value}
                                {fact.unit && fact.value !== missing ? (
                                    <span className="ml-1 text-xs font-semibold text-muted-foreground">
                                        {fact.unit}
                                    </span>
                                ) : (
                                    <></>
                                )}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
